import { AfterViewInit, Directive, ElementRef, HostListener, OnDestroy, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * Reformats the bound input's value to US-10-digit shape as the user
 * types: `(555) 555-5555`. Non-digit characters are stripped; extra
 * digits past 10 are dropped. Pastes, drags, and IME input all flow
 * through the same `input` event handler so any way a value arrives
 * gets normalized.
 *
 * The bound NgControl value is set to the FORMATTED string (not just
 * the digits) so downstream validation (`isInvalidPhone(value)` still
 * strips non-digits and counts) sees the exact same shape the user
 * sees. Persisted values carry the formatting through, which is what
 * receipts / envelopes / emails display later.
 *
 * Cursor position is preserved across the reformat: we count how many
 * digits sit before the caret and place it after the same number of
 * digits in the reformatted string. Without this, every keystroke
 * would send the caret to the end — painful for mid-edit corrections.
 *
 * Not intended for international numbers. If a partner types `+44 ...`
 * the leading `+` is stripped and the first 10 digits are formatted as
 * a US shape. Follow-up work if E.164 becomes a requirement.
 */
@Directive({
  selector: 'input[phoneFormat]',
  standalone: true,
})
export class PhoneFormatDirective implements AfterViewInit, OnDestroy {

  private valueChangesSub?: Subscription;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    // Optional so the directive can be attached to inputs that don't use
    // NgModel or FormControl — in that case we only update input.value.
    @Optional() private ngControl: NgControl | null,
  ) {}

  ngAfterViewInit(): void {
    // One-shot normalize of whatever value the input has after Angular
    // finishes writing the pre-fill (from `[(ngModel)]="foo"` where `foo`
    // was initialized on the parent). Without this the pre-filled value
    // would sit unformatted until the user touched the field.
    this.normalize(false);

    // Cover programmatic updates that arrive AFTER init — e.g., the
    // Primary Contact click that stamps `addForm.phone = userInfo.phone`,
    // or the Envelope-Sign flow pre-filling the guardian phone. Idempotent
    // via the equality short-circuit inside `normalize`, so the loop that
    // our own `setValue` triggers back into this subscription no-ops on
    // the second pass.
    const control = this.ngControl?.control;
    if (control) {
      this.valueChangesSub = control.valueChanges.subscribe(() => this.normalize(false));
    }
  }

  ngOnDestroy(): void {
    this.valueChangesSub?.unsubscribe();
  }

  @HostListener('input')
  onInput(): void {
    // User-driven change — preserve the caret position so mid-edit
    // corrections don't fling the cursor to the end of the field.
    this.normalize(true);
  }

  /**
   * Reads the current input value, reformats it to US phone shape, and
   * writes it back to both the DOM and the NgControl. When
   * `preserveCursor` is true, restores the caret to the equivalent
   * digit-relative position in the reformatted string. Skipped when the
   * value is already in the target shape (idempotency — this is what
   * breaks the potential valueChanges → normalize → setValue →
   * valueChanges loop).
   */
  private normalize(preserveCursor: boolean): void {
    const input = this.el.nativeElement;
    const currentValue = input.value || '';

    // Strip the US country code when the input carries 11 digits starting
    // with `1` — pre-fills like "+1 (555) 555-5555" or "15551234567"
    // otherwise land as "(155) 512-3456" via the naive `slice(0, 10)`,
    // silently losing the last real digit. Handling it here (not just at
    // pre-fill time) keeps behavior consistent when a user pastes a
    // country-code-prefixed number too.
    let digits = currentValue.replace(/\D/g, '');
    const strippedCountryCode = digits.length === 11 && digits.startsWith('1');
    if (strippedCountryCode) {
      digits = digits.slice(1);
    }
    digits = digits.slice(0, 10);
    const formatted = formatUsPhone(digits);

    if (formatted === currentValue) return;

    let newPos = formatted.length;
    if (preserveCursor) {
      const originalPos = input.selectionStart ?? currentValue.length;
      let digitsBeforeCursor = currentValue
        .slice(0, originalPos)
        .replace(/\D/g, '')
        .length;
      // If we removed a leading `1` and the caret was PAST that digit,
      // subtract one from the count so the caret lands in the right
      // slot in the newly-shifted number. Without this the caret drifts
      // one digit too far to the right after a country-code paste.
      if (strippedCountryCode && digitsBeforeCursor > 0) {
        digitsBeforeCursor -= 1;
      }
      newPos = 0;
      let count = 0;
      while (count < digitsBeforeCursor && newPos < formatted.length) {
        if (/\d/.test(formatted[newPos])) count++;
        newPos++;
      }
    }

    input.value = formatted;
    if (preserveCursor) {
      try {
        input.setSelectionRange(newPos, newPos);
      } catch {
        // Best-effort — throws on inputs that aren't focused or don't
        // support selection (edge cases in older Firefox). Non-fatal.
      }
    }

    if (this.ngControl?.control) {
      // emitEvent:true so [(ngModel)]'s viewToModelUpdate fires and the
      // parent's bound property lands with the formatted value — keeps
      // the DOM and the model in sync. The idempotency check above
      // prevents the valueChanges subscription from ping-ponging.
      this.ngControl.control.setValue(formatted, { emitEvent: true });
    }
  }
}

/**
 * Formats a 0-10 digit string into US phone shape. Partial input renders
 * as a partial format so the user sees the shape emerge as they type.
 *
 *   ''              → ''
 *   '5'             → '(5'
 *   '555'           → '(555'
 *   '5551'          → '(555) 1'
 *   '555123'        → '(555) 123'
 *   '5551234'       → '(555) 123-4'
 *   '5551234567'    → '(555) 555-1234'   (well, '(555) 123-4567')
 */
function formatUsPhone(digits: string): string {
  if (!digits) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}
